import { useEffect, useRef, useState } from "react";
import { ItemModel } from "../models/ItemModel";
import itemService from "../services/itemService";
import { set } from "date-fns";

interface EditInfoLinkCompProps {
    show: boolean;
    item: ItemModel;
    onClose: () => void;
    onInfoLinkUpdated: (updatedItem: ItemModel) => void;
}

interface InfoLinkFormErrors {
    infoLinkTitle?: string;
    infoLinkText?: string;
    infoLinkUrl?: string;
}

const EditInfoLinkComp: React.FC<EditInfoLinkCompProps> = ({
    show,
    item,
    onClose,
    onInfoLinkUpdated: onInfoLinkUpdated
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const [infoLinkTitle, setInfoLinkTitle] = useState(item.infoLinkTitle);
    const [infoLinkText, setInfoLinkText] = useState(item.infoLinkText);
    const [infoLinkUrl, setInfoLinkUrl] = useState(item.infoLinkUrl);


    const [isSubmitting, setIsSubmitting] = useState(false);
    const [infoLinkFormErrors, setInfoLinkFormErrors] = useState<InfoLinkFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setInfoLinkTitle(item.infoLinkTitle);
            setInfoLinkText(item.infoLinkText);
            setInfoLinkUrl(item.infoLinkUrl);
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateInfoLink = () => {
        const errors: InfoLinkFormErrors = {};
        if (!infoLinkTitle) {
            errors.infoLinkTitle = "Info link title is required";
        }

        if (!infoLinkUrl) {
            errors.infoLinkUrl = "Info link URL is required";
        } else if (!/^https?:\/\/.+/i.test(infoLinkUrl)) {
            errors.infoLinkUrl = "Info link URL must start with http:// or https://";
        }

        setInfoLinkFormErrors(errors);

        return Object.keys(errors).length === 0;
    }

    const handleInfoLinkSubmit = async (e: React.FormEvent) => {
        const updateInfoLink = async () => {
            try {
                const updatedItem = {
                    ...item,
                    infoLinkTitle: infoLinkTitle,
                    infoLinkText: infoLinkText,
                    infoLinkUrl: infoLinkUrl
                }
                const updateSucceed = await itemService.updateItem(updatedItem);
                if (updateSucceed === "Success") {
                    onInfoLinkUpdated(updatedItem);
                    setServiceStatus({ type: "success", message: "Info link updated successfully." });

                }
                else {
                    setServiceStatus({ type: "error", message: "Failed to update info link." });
                }
            } catch (error) {
                setServiceStatus({ type: "error", message: "Failed to update info link." });
            } finally {
                setIsSubmitting(false);
            }
        };


        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateInfoLink()) {
            setIsSubmitting(true);
            updateInfoLink();
        }
    }



    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Info & Support Link</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-md-12">
                                <label className="form-label">Link Title</label>
                                <input
                                    type="text"
                                    className={`form-control ${infoLinkFormErrors.infoLinkTitle ? 'is-invalid' : ''}`}
                                    value={infoLinkTitle}
                                    onChange={(e) => setInfoLinkTitle(e.target.value)}
                                />
                                {infoLinkFormErrors.infoLinkTitle && <div className="invalid-feedback">{infoLinkFormErrors.infoLinkTitle}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Link URL</label>
                                <input
                                    type="text"
                                    className={`form-control ${infoLinkFormErrors.infoLinkUrl ? 'is-invalid' : ''}`}
                                    value={infoLinkUrl}
                                    onChange={(e) => setInfoLinkUrl(e.target.value)}
                                />
                                {infoLinkFormErrors.infoLinkUrl && <div className="invalid-feedback">{infoLinkFormErrors.infoLinkUrl}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className={`form-control ${infoLinkFormErrors.infoLinkText ? 'is-invalid' : ''}`}
                                    value={infoLinkText}
                                    onChange={(e) => setInfoLinkText(e.target.value)}
                                />
                                {infoLinkFormErrors.infoLinkText && <div className="invalid-feedback">{infoLinkFormErrors.infoLinkText}</div>}
                            </div>
                        </div>
                        {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark rounded-0" onClick={handleInfoLinkSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    );
};
export default EditInfoLinkComp;